import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
  Button,
  UncontrolledTooltip,
  Modal,
  ModalBody,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import { MessageCircle, Trash2, UploadCloud } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAiKnowledgeBaseRecords, deleteAiKnowledgeBaseRecords } from '../ReduxToolkit/Reducers/aiKnowledgeBase';
import ChatModal from '../Components/ChatModal';

import FileIcon from '../assets/file-png.png'

// Status list for filtering
const statusList = [
  { id: 'all', label: 'All Status' },
  { id: 'pending', label: 'Pending' },
  { id: 'processing', label: 'Processing' },
  { id: 'completed', label: 'Completed' },
  { id: 'failed', label: 'Failed' }
];

function TrainAI() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { records, totalRecords, isLoading, deleteLoading } = useSelector((state) => state.aiKnowledgeBase);

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [tableData, setTableData] = useState([]);

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');

  // Modal states
  const [deleteModal, setDeleteModal] = useState(false);
  const [chatModal, setChatModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (page && user?.id) {
      _getTableData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, dateFilter, itemsPerPage, user]);

  useEffect(() => {
    if (records[page]) {
      setTableData(records[page]);
    }
  }, [records, page]);

  const _getTableData = async (hideLoader = false) => {
    if (user?.id) {
      const status = statusFilter === 'all' ? '' : statusFilter;
      await dispatch(getAiKnowledgeBaseRecords(page, itemsPerPage, status, dateFilter, '', '', hideLoader));
    }
  };

  const handleItemPerPageSelect = async (count) => {
    setPage(1);
    setItemsPerPage(count);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handleDateFilter = (e) => {
    setDateFilter(e.target.value);
    setPage(1);
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setDateFilter('');
    setPage(1);
  };

  const handleDeleteRecord = (record) => {
    setSelectedRecord(record);
    setSelectedIds([record.id]);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (selectedIds.length === 0) return;

    const result = await dispatch(deleteAiKnowledgeBaseRecords(selectedIds));

    if (result?.statusCode === 200) {
      setDeleteModal(false);
      setSelectedRecord(null);
      setSelectedIds([]);
      _getTableData(true);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'pending': return 'info';
      case 'failed':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const columns = [
    {
      name: 'Date',
      selector: (row) => row.created_at,
      sortable: true,
      minWidth: '90px',
      cell: (row) => (
        <span style={{ fontSize: '13px', color: '#495057' }}>
          {new Date(row.created_at).toLocaleDateString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric'
          })}
        </span>
      ),
    },
    {
      name: 'Files Name',
      minWidth: '100px',
      wrap: false,
      cell: (row) => row.file_name,
    },
    {
      name: 'Files Type',
      minWidth: '100px',
      wrap: false,
      cell: (row) => row.file_type ? row.file_type?.split('/')[1] : '--',
    },
    {
      name: 'Mode',
      selector: (row) => row.training_mode,
      sortable: true,
      minWidth: '120px',
      cell: (row) => row.training_mode ? (
        <div style={{ backgroundColor: '#D9D9D982', color: '#212121', fontSize: '12px', fontWeight: '500', padding: '4px 15px', borderRadius: '20px' }}>
          {row.training_mode}
        </div>
      ) : '--',
    },
    {
      name: 'Status',
      selector: (row) => row.status,
      sortable: true,
      minWidth: '130px',
      cell: (row) => {
        const status = row?.status?.toLowerCase() || '';
        return row?.status ? (
          <div style={{ backgroundColor: status === 'completed' ? '#F0F9F4' : '#D9D9D933', color: status === 'completed' ? '#05A150' : '#212121', fontSize: '12px', fontWeight: '500', padding: '4px 15px', borderRadius: '20px' }}>
            {row.status}
          </div>
        ) : '--'
      },
    },
    {
      name: 'Training Duration',
      selector: (row) => row.training_duration,
      sortable: true,
      minWidth: '150px',
      cell: (row) => (
        <span style={{ fontSize: '13px', color: '#495057' }}>
          {row.training_duration || '-'}
        </span>
      ),
    },
    {
      name: 'Actions',
      minWidth: '120px',
      cell: (row) => (
        <div className="d-flex gap-2">
          <button
            size="sm"
            id={`delete-${row.id}`}
            onClick={() => handleDeleteRecord(row)}
            className='btn'
            style={{
              padding: '4px 4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Trash2 size={20} />
          </button>
          <UncontrolledTooltip placement="top" target={`delete-${row.id}`}>
            Delete Record
          </UncontrolledTooltip>
          <button
            size="sm"
            id={`test-${row.id}`}
            className='btn'
            onClick={() => setChatModal(true)}
            style={{
              padding: '4px 4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MessageCircle size={20} />
          </button>
          <UncontrolledTooltip placement="top" target={`test-${row.id}`}>
            Test the chat model
          </UncontrolledTooltip>
        </div>
      ),
    },
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#f8f9fa',
        borderBottom: '2px solid #dee2e6',
        fontWeight: '600',
        fontSize: '13px',
      },
    },
    rows: {
      style: {
        minHeight: '60px',
        '&:hover': {
          backgroundColor: '#f8f9fa',
          cursor: 'pointer',
        },
      },
    },
  };

  const paginationComponentOptions = {
    rowsPerPageText: 'Rows per page',
    rangeSeparatorText: 'of',
    selectAllRowsItem: false,
  };

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <Col sm={12}>
            <Card>
              <CardBody>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h4 className="mb-1" style={{ fontWeight: '600', fontSize: '20px' }}>
                      Vionne Knowledge Base Training
                    </h4>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                      Track all the custom knowledge base training of Vionne
                    </p>
                  </div>
                  <Button
                    color="primary"
                    onClick={() => navigate('/train-vionne')}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <UploadCloud size={18} />
                    Train Vionne
                  </Button>
                </div>

                {/* Filters and Bulk Actions */}
                <Row className="mb-3">
                  <Col md={6}>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex gap-2 justify-content-end align-items-center">
                      {(statusFilter !== 'all' || dateFilter) && (
                        <Button
                          color="outline-primary"
                          onClick={clearFilters}
                          style={{ border: '1px solid #dee2e6', fontSize: '14px' }}
                        >
                          Clear Filters
                        </Button>
                      )}
                      <UncontrolledDropdown>
                        <DropdownToggle
                          caret
                          color="light"
                          style={{
                            minWidth: '150px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            border: '1px solid #dee2e6'
                          }}
                        >
                          <span style={{ fontSize: '14px' }}>
                            {statusList.find(s => s.id === statusFilter)?.label || 'All Status'}
                          </span>
                        </DropdownToggle>
                        <DropdownMenu end>
                          {statusList.map((status) => (
                            <DropdownItem
                              key={status.id}
                              onClick={() => handleStatusFilter(status.id)}
                              active={statusFilter === status.id}
                            >
                              {status.label}
                            </DropdownItem>
                          ))}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                      <div style={{ position: 'relative', minWidth: '180px' }}>
                        <Input
                          type="date"
                          value={dateFilter}
                          onChange={handleDateFilter}
                          placeholder="Filter by date"
                          style={{ fontSize: '14px' }}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Data Table */}
                <DataTable
                  columns={columns}
                  data={tableData}
                  progressPending={isLoading}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRecords}
                  paginationDefaultPage={page}
                  onChangePage={(newPage) => setPage(newPage)}
                  onChangeRowsPerPage={handleItemPerPageSelect}
                  paginationPerPage={itemsPerPage}
                  paginationComponentOptions={paginationComponentOptions}
                  customStyles={customStyles}
                  highlightOnHover
                  pointerOnHover
                  responsive
                  noDataComponent={
                    <div className="text-center py-5 d-flex flex-column gap-3 justify-content-center align-items-center">
                      <h4>No Training History Yet</h4>
                      <p>You haven’t trained the Vionne Search Agent yet on any documents. Upload knowledge base <br /> documents to start improving search accuracy.</p>
                      <Button
                        color="primary"
                        onClick={() => navigate('/train-vionne')}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        <UploadCloud size={18} />
                        Train Vionne
                      </Button>
                    </div>
                  }
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered style={{ maxWidth: '420px' }}>
          <ModalBody style={{ padding: '36px 32px 32px', textAlign: 'center', position: 'relative', borderRadius: '20px' }}>
            <button
              onClick={() => setDeleteModal(false)}
              style={{
                position: 'absolute', top: '14px', right: '14px',
                background: '#212121', border: 'none', borderRadius: '50%',
                width: '28px', height: '28px', display: 'flex', alignItems: 'center',
                justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: '16px', lineHeight: 1
              }}
            >
              ×
            </button>

            {/* Icon */}
            <div style={{ marginBottom: '20px' }}>
              <img src={FileIcon} alt="File Icon" style={{ width: '55px', height: '64px' }} />
            </div>

            <h5 style={{ fontWeight: '700', fontSize: '18px', color: '#212121', marginBottom: '12px' }}>
              Remove from Search Model?
            </h5>
            <p style={{ fontSize: '13px', color: '#6c757d', lineHeight: '1.6', marginBottom: '28px' }}>
              You are about to remove this knowledge base from the Smart Search model. Once removed, the Smart Search Agent will no longer use this data to generate responses.
            </p>

            <div style={{ display: 'flex', gap: '12px' }}>
              <Button
                onClick={() => setDeleteModal(false)}
                disabled={deleteLoading}
                outline
                style={{
                  flex: 1, padding: '12px', fontSize: '15px', fontWeight: '500',
                  border: '1px solid #dee2e6', backgroundColor: '#fff', color: '#212121'
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={deleteLoading}
                color='primary'
                style={{
                  flex: 1, padding: '12px', fontSize: '15px', fontWeight: '600',
                  backgroundColor: '#212121', border: 'none', color: '#fff'
                }}
              >
                {deleteLoading && <Spinner size="sm" className="me-2" />}
                {deleteLoading ? 'Removing...' : 'Remove'}
              </Button>
            </div>
          </ModalBody>
        </Modal>

        <ChatModal isOpen={chatModal} toggle={() => setChatModal(false)} />
      </Container>
    </div>
  );
}

export default TrainAI;
